import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    // Use capture so we receive touch/pointer on mobile before any child (e.g. img); ensures swipe works
    this.addEventListener('pointerdown', this.#handlePointerDown, { passive: false, capture: true });

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.dataset.initialSlide ? parseInt(this.dataset.initialSlide, 10) : 0;

    let visibleSlidesAmount = 0;

    if (this.current !== 0) {
      this.select(this.current, undefined, { animate: false });
      visibleSlidesAmount = 1;
    } else {
      visibleSlidesAmount = this.#updateVisibleSlides();
      if (visibleSlidesAmount === 0) {
        this.select(0, undefined, { animate: false });
        visibleSlidesAmount = 1;
      }
    }

    this.#resizeObserver = new ResizeObserver(async () => {
      if (viewTransition.current) await viewTransition.current;

      if (visibleSlidesAmount > 1) {
        this.#updateVisibleSlides();
      }

      if (this.hasAttribute('auto-hide-controls')) {
        this.#updateControlsVisibility();
      }
    });

    this.#resizeObserver.observe(this.refs.slideshowContainer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('pointerdown', this.#handlePointerDown, { capture: true });
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#scroll?.destroy();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get defaultSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    // Guard if invalid
    if (requestedIndex === undefined || isNaN(requestedIndex)) return;

    const { slides } = this;

    if (!slides?.length) return;
    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { current } = this;
    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      const currentSlide = slides[current];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');
    this.#scroll.to(slide, { instant });
    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    const axis = this.#scroll?.axis ?? 'x';
    return getVisibleElements(this.refs.scroller, this.slides, SLIDE_VISIBLITY_THRESHOLD, axis);
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    if (this.closest('.card-gallery.card-gallery--arrow-nav')) return;

    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    // Multi-column PDP galleries: individual slides rarely meet the 0.7 visibility
    // threshold, so fall back to closest slide by scroll offset (avoids snap-back).
    if (!visibleSlides.length) {
      return (this.current = this.#closestSlideIndexByScroll());
    }

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = this.#closestSlideIndexByScroll());

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  /**
   * Finds the slide whose start edge is closest to the current scroll position.
   * Used when intersection-based visibility cannot resolve a slide (multi-visible).
   * @returns {number}
   */
  #closestSlideIndexByScroll() {
    const slides = this.slides;
    const { scroller } = this.refs;
    if (!slides?.length || !scroller) return this.current;

    const { axis } = this.#scroll;
    const scrollPos = axis === 'x' ? scroller.scrollLeft : scroller.scrollTop;
    let bestIndex = this.current || 0;
    let bestDist = Infinity;

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      if (!slide || slide.hasAttribute('hidden')) continue;
      const pos = axis === 'x' ? slide.offsetLeft : slide.offsetTop;
      const dist = Math.abs(pos - scrollPos);
      if (dist < bestDist) {
        bestDist = dist;
        bestIndex = i;
      }
    }

    return bestIndex;
  }

  #dragging = false;

  /**
   * Handles the 'pointerdown' event to start dragging/swiping slides (mouse and touch).
   * @param {PointerEvent} event - The pointerdown event.
   */
  #handlePointerDown = (event) => {
    const cardGallery = this.closest('.card-gallery');
    if (cardGallery?.classList.contains('card-gallery--arrow-nav')) return;

    // Card gallery on touch: do not capture – let native overflow scroll handle swipe so touch swipe works reliably.
    if (cardGallery && event.pointerType === 'touch') {
      return;
    }

    // PDP full-width gallery on touch: native momentum scroll (smoother than JS pointer drag).
    const pdpFullWidthGallery = this.closest('.product-information__media--full-width');
    if (pdpFullWidthGallery && event.pointerType === 'touch') {
      return;
    }

    // Nested card-gallery slideshows must not steal drag from parent carousels
    // (Recently Viewed / You May Also Like / resource-list).
    if (cardGallery && this.isNested) return;

    // Mouse: only primary button; ignore right/middle click.
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    // Use refs.slides (all DOM slides) so touch drag works after variant filtering hides some
    // slides. this.slides filters by hidden/reveal, so it can be length 1 and block touch.
    const slides = this.refs.slides;
    const scroller = this.refs.scroller;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    // Let real controls (play, form fields) receive clicks without starting a drag.
    if (event.target.closest('button, input, select, textarea, label')) {
      return;
    }

    // Resolve drag axis from real overflow. Prefer X when the scroller can move
    // horizontally — slight vertical overflow must not steal mouse drag on PDP.
    let axis = this.#scroll.axis;
    if (scroller) {
      const canScrollX = scroller.scrollWidth > scroller.clientWidth + 1;
      const canScrollY = scroller.scrollHeight > scroller.clientHeight + 1;
      if (!canScrollX && !canScrollY) return;
      axis = canScrollX ? 'x' : 'y';
    }

    // Free-scroll product carousels (recs / recently viewed): inertia + fast snap.
    // PDP single-scroll keeps discrete slide settle.
    const isFreeScrollCarousel =
      !this.classList.contains('slideshow--single-scroll') &&
      (this.classList.contains('resource-list__carousel') ||
        Boolean(this.closest('.resource-list__carousel')) ||
        this.classList.contains('slideshow--multiple-visible'));

    // Do not preventDefault here so tap (no drag) still fires click for card navigation.
    // Click is suppressed in onPointerMove when user actually drags (see document.addEventListener('click', preventDefault)).
    // Store initial position (pointer events: clientX/clientY for x/y axis)
    const startPosition = axis === 'x' ? event.clientX : event.clientY;

    const controller = new AbortController();
    const { signal } = controller;
    let previous = startPosition;
    let lastMoveTime = performance.now();
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    // Desktop: native HTML image/link drag steals the gesture and blocks pointermove.
    // Kill dragstart for this gesture so mouse click-drag can swipe the gallery.
    document.addEventListener('dragstart', preventDefault, { capture: true, signal });

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = axis === 'x' ? event.clientX : event.clientY;
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);
        event.preventDefault(); // Prevent page scroll/selection once drag starts

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
        if (scroller) scroller.style.scrollBehavior = 'auto';
      }

      event.preventDefault(); // Prevent page scroll/selection during drag
      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const now = performance.now();
      const delta = previous - current;
      const timeDelta = Math.max(1, now - lastMoveTime);
      // Frame-to-frame velocity (px/s) — total-drag duration made flings feel dead.
      velocity = (delta / timeDelta) * 1000;
      lastMoveTime = now;
      previous = current;
      distanceTravelled += Math.abs(delta);

      // Write scroll position directly so mouse drag moves every frame (no scrollBy no-op).
      if (scroller) {
        if (axis === 'x') scroller.scrollLeft += delta;
        else scroller.scrollTop += delta;
      } else {
        this.#scroll.by(delta, { instant: true });
      }
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      // Decay stale velocity if the pointer paused before release
      if (performance.now() - lastMoveTime > 80) velocity = 0;

      const direction = Math.sign(velocity);
      const lastIndex = slides.length - 1;
      let newIndex = this.current;

      if (isFreeScrollCarousel) {
        // Inertia carries the track; then snap to the nearest card (no extra jump).
        if (Math.abs(velocity) > 80) {
          await this.#inertiaScroll(scroller, axis, velocity);
        }
        newIndex = this.#sync();
      } else {
        const next = this.#sync();
        const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
        if (modifier === 0) {
          newIndex = next;
        } else if (this.infinite) {
          if (next + modifier < 0) newIndex = lastIndex;
          else if (next + modifier > lastIndex) newIndex = 0;
          else newIndex = next + modifier;
        } else {
          newIndex = clamp(next + modifier, 0, lastIndex);
        }
      }

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      // Recs carousels: settle instantly for a snappier feel; PDP keeps smooth settle.
      this.#scroll.to(newSlide, { instant: isFreeScrollCarousel || prefersReducedMotion() });

      this.removeAttribute('dragging');
      try {
        this.releasePointerCapture(event.pointerId);
      } catch {
        // Pointer may never have been captured (tap without move)
      }

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      if (!isFreeScrollCarousel) {
        await this.#scroll.finished;
      }

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      scroller.style.removeProperty('scroll-behavior');
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * Abort drag on a *new* pointerdown (e.g. opening zoom while still "dragging").
     * Must use capture:true and ignore this same pointerId — otherwise the current
     * pointerdown bubbles to document and immediately calls onPointerUp, killing
     * mouse drag before any pointermove (touch still works via native overflow scroll).
     */
    document.addEventListener(
      'pointerdown',
      (nextEvent) => {
        if (nextEvent.pointerId === event.pointerId) return;
        onPointerUp(nextEvent);
      },
      { signal, capture: true }
    );
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  /**
   * Decaying inertia after a mouse flick on free-scroll carousels.
   * @param {HTMLElement} scroller
   * @param {'x' | 'y'} axis
   * @param {number} velocityPxPerSec
   * @returns {Promise<void>}
   */
  #inertiaScroll(scroller, axis, velocityPxPerSec) {
    return new Promise((resolve) => {
      // Convert px/s → px/frame at ~60fps, then soften for control
      let v = clamp(velocityPxPerSec / 60, -90, 90) * 0.85;
      const friction = 0.9;
      const minV = 0.4;
      const maxScroll =
        axis === 'x'
          ? Math.max(0, scroller.scrollWidth - scroller.clientWidth)
          : Math.max(0, scroller.scrollHeight - scroller.clientHeight);

      const step = () => {
        if (Math.abs(v) < minV) {
          resolve();
          return;
        }

        if (axis === 'x') {
          scroller.scrollLeft = clamp(scroller.scrollLeft + v, 0, maxScroll);
          if (scroller.scrollLeft <= 0 || scroller.scrollLeft >= maxScroll) {
            resolve();
            return;
          }
        } else {
          scroller.scrollTop = clamp(scroller.scrollTop + v, 0, maxScroll);
          if (scroller.scrollTop <= 0 || scroller.scrollTop >= maxScroll) {
            resolve();
            return;
          }
        }

        v *= friction;
        requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    });
  }

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    return this.dataset.initialSlide ? parseInt(this.dataset.initialSlide, 10) : 0;
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Update aria-hidden based on visibility
    slides.forEach((slide) => {
      const isVisible = visibleSlides.includes(slide);
      slide.setAttribute('aria-hidden', `${!isVisible}`);
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
